import { Fragment, type ReactNode } from "react"
import { cn } from "@/lib/utils"

/** Only http(s) links are safe to render as an href — blocks javascript:/data: URIs. */
export function isSafeHttpUrl(url: string | null | undefined): url is string {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim())
}

const LINK_CLASS =
  "font-semibold text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"

export function AdminLinkedInLink({
  href,
  className,
}: {
  href?: string | null
  className?: string
}) {
  if (!isSafeHttpUrl(href)) return null
  return (
    <a
      href={href.trim()}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(LINK_CLASS, className)}
    >
      LinkedIn ↗
    </a>
  )
}

/**
 * Operator-facing person mention: name, optional email, and LinkedIn when
 * the joined profile has one.
 */
export function AdminPersonLine({
  name,
  email,
  linkedinUrl,
  role,
}: {
  name?: string | null
  email?: string | null
  linkedinUrl?: string | null
  role?: string | null
}) {
  const parts: Array<{ key: string; node: ReactNode }> = []
  if (role) parts.push({ key: "role", node: role })
  parts.push({ key: "name", node: name?.trim() || "Unknown" })
  if (email) parts.push({ key: "email", node: email })
  if (isSafeHttpUrl(linkedinUrl)) {
    parts.push({
      key: "linkedin",
      node: <AdminLinkedInLink href={linkedinUrl} />,
    })
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      {parts.map((part, i) => (
        <Fragment key={part.key}>
          {i > 0 && <span aria-hidden>·</span>}
          <span>{part.node}</span>
        </Fragment>
      ))}
    </span>
  )
}
